import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { BasicChip } from '@/components/Chips/BasicChip';
import { DcrType } from '@/core/graphql/generated/types';

import { formatDiffingValue } from '../../_utils/formatDiffingValue';
import { reviewDiffStyles } from '../ReviewModalContent.styles';

import { reviewRowStyles as sx } from './ReviewRow.styles';

type ReviewRowProps = {
	labelKey: string;
	oldVal: unknown;
	newVal: unknown;
	fieldTranslations: Record<string, string> | undefined;
	dcrType: DcrType;
	chip?: string;
};

const isEmpty = (val: unknown) =>
	val === null || val === undefined || val === '';

export const ReviewRow = ({
	labelKey,
	oldVal,
	newVal,
	fieldTranslations,
	dcrType,
	chip
}: ReviewRowProps) => {
	const { t } = useTranslation();

	const displayLabel =
		labelKey === 'id'
			? '' // Placeholder
			: (fieldTranslations?.[labelKey] ?? t(labelKey));

	const isChange = dcrType === DcrType.Change;
	const isAdded = isChange && isEmpty(oldVal);
	const isRemoved = isChange && !isEmpty(oldVal) && isEmpty(newVal);

	return (
		<Box sx={sx.row}>
			{displayLabel && (
				<Typography sx={reviewDiffStyles.changeLabel}>
					{displayLabel}
				</Typography>
			)}

			<Box sx={reviewDiffStyles.changeRow}>
				{isAdded && (
					<>
						<Typography
							component='span'
							variant='body2'
							sx={[reviewDiffStyles.tag, reviewDiffStyles.addedTag]}
						>
							{t('Added')}
						</Typography>
						<Typography
							component='span'
							variant='body2'
							sx={reviewDiffStyles.newValue}
						>
							{formatDiffingValue(newVal)}
						</Typography>
					</>
				)}

				{isRemoved && (
					<>
						<Typography
							component='span'
							variant='body2'
							sx={[reviewDiffStyles.tag, reviewDiffStyles.removedTag]}
						>
							{t('Removed')}
						</Typography>
						<Typography
							component='span'
							variant='body2'
							sx={reviewDiffStyles.removedValue}
						>
							{formatDiffingValue(oldVal)}
						</Typography>
					</>
				)}

				{!isAdded && !isRemoved && (
					<>
						{isChange && (
							<>
								<Typography
									component='span'
									variant='body2'
									sx={reviewDiffStyles.previousValue}
								>
									{formatDiffingValue(oldVal)}
								</Typography>
								<ArrowRightAltIcon sx={reviewDiffStyles.arrowIcon} />
							</>
						)}
						<Typography
							component='span'
							variant='body2'
							sx={reviewDiffStyles.newValue}
						>
							{formatDiffingValue(newVal)}
						</Typography>
					</>
				)}

				{chip && <BasicChip label={chip} variant='basic' />}
			</Box>
		</Box>
	);
};
